import { cn } from '@commenda/utils';
import * as React from 'react';
import { ElementType, ReactNode, useLayoutEffect, useState } from 'react';
import { Tooltip } from '../Tooltip/Tooltip';

interface TruncatedTextProps {
  /**
   * The text (or any inline content) to render. When this overflows its
   * container, it gets ellipsised and a tooltip is enabled on hover.
   */
  children: ReactNode;
  /** Extra classes appended to the truncating element. */
  className?: string;
  /**
   * Override the tooltip content. Defaults to `children`. Useful when
   * `children` is JSX but you want the tooltip to show plain text.
   */
  tooltipContent?: ReactNode;
  /** Tooltip side — passed through to the underlying Tooltip. Default `top`. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Element type to render. Defaults to `span`. Use `p` for block-level
   * paragraph behavior; ensure the parent constrains width either way.
   */
  as?: Extract<ElementType, 'span' | 'p' | 'div'>;
}

const TruncatedText = ({
  children,
  className,
  tooltipContent,
  side = 'top',
  as: Tag = 'span',
}: TruncatedTextProps) => {
  // Keep the node in state, not a ref: the Tooltip remounts it whenever
  // `disabled` flips (truncated ⇄ not), so the node identity changes. Keying
  // the effect on it means we always (re)attach the observer to the live node.
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // useLayoutEffect (not useEffect): measure before paint so a truncated value
  // never flashes its tooltip-less state for a frame before isTruncated settles.
  useLayoutEffect(() => {
    if (!node) return undefined;
    const measure = () => setIsTruncated(node.scrollWidth > node.clientWidth);
    measure();
    // The box changes — and we re-measure — on column resize, container
    // reflow, and the fallback→web-font swap (which grows the line until it
    // overflows). That covers every way truncation turns on or off.
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, children, className]);

  return (
    <Tooltip
      side={side}
      content={tooltipContent ?? children}
      disabled={!isTruncated}
      asChild
    >
      <Tag
        ref={setNode}
        // min-w-0 overrides a flex child's default min-width:auto so truncate kicks in.
        className={cn('block min-w-0 truncate', className)}
      >
        {children}
      </Tag>
    </Tooltip>
  );
};

export { TruncatedText };
export type { TruncatedTextProps };
